/**
 * Domain Management - Club Domain Management Component
 * Allows club admins to view and manage their domains (automatic subdomain + custom domains)
 *
 * Features:
 * - View automatic subdomain (created with club)
 * - Add custom domains
 * - Check real-time SSL status
 * - Delete custom domains
 * - Real-time status polling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertCircle, Trash2, Plus, X } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, Section, Card, FormInput, Button, Badge } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';

const DomainManagement = () => {
  const { showSuccess, showError } = useToast();
  const [club_id, setClubId] = useState(null);
  const [domains, setDomains] = useState([]);
  const [automaticDomain, setAutomaticDomain] = useState(null);
  const [customDomains, setCustomDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pollingDomains, setPollingDomains] = useState(new Set());
  const [autoPolling, setAutoPolling] = useState(true);

  // Form state for adding custom domain
  const [customDomainInput, setCustomDomainInput] = useState('');
  const [customDomainError, setCustomDomainError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Confirmation Dialog State
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  /**
   * Initialize club ID from current user
   */
  useEffect(() => {
    const initializeClubId = async () => {
      try {
        const userInfo = await apiService.getCurrentUser();
        const club_id = userInfo.club_id;

        if (!club_id) {
          throw new Error('No club associated with your account');
        }

        setClubId(club_id);
      } catch (err) {
        console.error('Error initializing club ID:', err);
        showError('Failed to initialize');
      }
    };

    initializeClubId();
  }, [showError]);

  /**
   * Load domains for club
   */
  const loadDomains = useCallback(async (includeStatus = true) => {
    try {
      setLoading(true);

      const response = await apiService.get(
        `/api/clubs/${club_id}/domains`,
        { include_status: includeStatus }
      );

      setDomains(response.domains || []);
      setAutomaticDomain(response.automatic_subdomain || null);
      setCustomDomains(response.custom_domains || []);

    } catch (err) {
      console.error('Error loading domains:', err);
      showError('Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, [club_id, showError]);

  /**
   * Get status for a specific domain
   */
  const checkDomainStatus = useCallback(async (domainId) => {
    try {
      const response = await apiService.post(
        `/api/clubs/${club_id}/domains/${domainId}/check-status`,
        {}
      );

      // Update domain in state
      setDomains(prev => prev.map(d => {
        if (d.id === domainId) {
          return {
            ...d,
            status: response.details
          };
        }
        return d;
      }));

      return response;
    } catch (err) {
      console.error(`Error checking status for domain ${domainId}:`, err);
      throw err;
    }
  }, [club_id]);

  /**
   * Poll status for domains that are not fully ready
   */
  useEffect(() => {
    if (!autoPolling) return;

    const domainsNeedingStatus = domains.filter(d => {
      const status = d.status;
      return !status?.ready || !status?.ssl_provisioned;
    });

    if (domainsNeedingStatus.length === 0) {
      setPollingDomains(new Set());
      setAutoPolling(false);
      return;
    }

    const pollInterval = setInterval(() => {
      domainsNeedingStatus.forEach(domain => {
        if (!pollingDomains.has(domain.id)) {
          setPollingDomains(prev => new Set([...prev, domain.id]));
          checkDomainStatus(domain.id)
            .catch(() => {
              // Continue polling even if check fails
            });
        }
      });
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [domains, autoPolling, pollingDomains, checkDomainStatus]);

  /**
   * Load domains on mount and when club_id is available
   */
  useEffect(() => {
    if (club_id) {
      loadDomains(true);
    }
  }, [club_id, loadDomains]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  /**
   * Validate domain input
   */
  const validateDomainInput = (input) => {
    if (!input || input.trim().length === 0) {
      return 'Domain is required';
    }
    if (input.trim().length < 3) {
      return 'Domain must be at least 3 characters';
    }
    if (input.trim().length > 255) {
      return 'Domain must be no more than 255 characters';
    }
    const domainRegex = /^[a-z0-9.-]+$/i;
    if (!domainRegex.test(input)) {
      return 'Domain must contain only letters, numbers, hyphens, and dots';
    }
    return null;
  };

  /**
   * Handle adding custom domain
   */
  const handleAddDomain = async (e) => {
    e.preventDefault();

    // Validate input
    const validationError = validateDomainInput(customDomainInput);
    if (validationError) {
      setCustomDomainError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setCustomDomainError('');

      const response = await apiService.post(
        `/api/clubs/${club_id}/domains`,
        { domain: customDomainInput.trim() }
      );

      showSuccess(response.message || 'Custom domain created successfully');
      setCustomDomainInput('');
      setShowAddForm(false);

      // Reload domains
      await loadDomains(true);

    } catch (err) {
      console.error('Error adding domain:', err);
      setCustomDomainError(err.message || 'Failed to add custom domain');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle Confirm Delete
   */
  const handleConfirmDelete = async () => {
    if (!showDeleteModal) return;

    try {
      setSubmitting(true);
      await apiService.delete(
        `/api/clubs/${club_id}/domains/${showDeleteModal.id}`,
        { confirm: true }
      );

      showSuccess(`Domain ${showDeleteModal.domain} deleted successfully`);

      // Reload domains
      await loadDomains(true);
      setShowDeleteModal(null);

    } catch (err) {
      console.error('Error deleting domain:', err);
      showError(err.message || 'Failed to delete domain');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Get status color and label
   */
  const getStatusColor = (domain) => {
    const status = domain.status;
    if (!status) return 'secondary';
    if (status.ready && status.ssl_provisioned) return 'success';
    if (status.ready && !status.ssl_provisioned) return 'warning';
    if (status.status === 'provisioning') return 'info';
    return 'danger';
  };

  const getStatusLabel = (domain) => {
    const status = domain.status;
    if (!status) return 'Unknown';
    if (status.ready && status.ssl_provisioned) return 'Ready';
    if (status.ready && !status.ssl_provisioned) return 'SSL Provisioning...';
    if (status.status === 'provisioning') return 'Provisioning...';
    return 'Error';
  };

  /**
   * Render all domains (automatic + custom) for listing
   */
  const allDisplayDomains = [
    ...(automaticDomain ? [automaticDomain] : []),
    ...customDomains
  ];

  const filteredDomains = allDisplayDomains.filter(domain => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!domain.domain?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    return true;
  });

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search by domain name..."
      filters={[]}
      activeFilters={{}}
      onFilterChange={() => { }}
      onClearFilters={() => setFilters({ search: '', status: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        !showAddForm && customDomains.length < 10 && (
          <CreateAction
            onClick={() => setShowAddForm(true)}
            label="Add Domain"
          />
        )
      }
    />
  );

  return (
    <AdminPageLayout
      title="DOMAIN ARCHITECTURE"
      subtitle="Governing global connectivity and secure gateways"
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
        {/* Information section */}
        <Section title="Connectivity Protocol">
          <Card level={1} variant="feature">
            <h3 style={{ marginTop: 0, marginBottom: '1.25rem', fontSize: '1.15rem', fontWeight: '700' }}>SSL Certificate Status</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '1rem', opacity: 0.8 }}>
              <strong>New domains take 15-60 minutes for SSL provisioning.</strong>
            </p>
            <ul style={{ margin: '1rem 0', paddingLeft: '1.5rem', fontSize: '0.95rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', opacity: 0.8 }}>
              <li>Domain mapping is created immediately across our edge network.</li>
              <li>SSL certificate provisioning begins automatically upon detection.</li>
              <li>Operational delay of 15-60 minutes typically expected for full propagation.</li>
              <li>Once SSL is active, the domain will be fully operational and secure.</li>
            </ul>
          </Card>
        </Section>

        {/* Add domain form */}
        {showAddForm && (
          <Section title="Provision New Domain">
            <Card level={1}>
              <form onSubmit={handleAddDomain} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div>
                  <FormInput
                    label="Target Domain"
                    placeholder="e.g., metrosoccer.com"
                    value={customDomainInput}
                    onChange={(e) => {
                      setCustomDomainInput(e.target.value);
                      setCustomDomainError('');
                    }}
                    error={customDomainError}
                    helpText="Enter a secondary top-level domain or enterprise-grade endpoint."
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Button
                    type="submit"
                    disabled={submitting || !customDomainInput.trim()}
                    loading={submitting}
                    variant="save"
                  >
                    Provision Domain
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setCustomDomainInput('');
                      setCustomDomainError('');
                    }}
                    disabled={submitting}
                    variant="cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </Section>
        )}

        <Section title="Identifier Index">
          <Listing
            loading={loading}
            items={filteredDomains}
            actions={headerActions}
            filters={filterBar}
            variant="list"
            renderItem={(domain) => {
              const isAutomatic = domain.id === automaticDomain?.id;
              const status = domain.status;
              const statusVariant = (status?.ready && status?.ssl_provisioned) ? 'success' : (status?.status === 'provisioning' ? 'primary' : 'warning');

              return (
                <ListingItem
                  key={domain.id}
                  title={domain.domain}
                  subtitle={isAutomatic ? 'System-Generated Gateway' : 'Custom Enterprise Endpoint'}
                  icon={status?.ready && status?.ssl_provisioned ? <CheckCircle size={32} /> : <AlertCircle size={32} />}
                  iconVariant={getStatusColor(domain)}
                  actions={
                    !isAutomatic && (
                      <DeleteAction size="sm" onClick={() => setShowDeleteModal(domain)} />
                    )
                  }
                >
                  <div style={{ display: 'flex', gap: '2.5rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>Provisioned Date</span>
                      <span style={{ fontWeight: 500 }}>{new Date(domain.created_at).toLocaleDateString()}</span>
                    </div>
                    {status && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>Mapping Status</span>
                          <Badge variant={status.ready ? 'success' : 'warning'} size="sm">
                            {status.ready ? 'READY' : 'PROVISIONING'}
                          </Badge>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>SSL Security</span>
                          <Badge variant={status.ssl_provisioned ? 'success' : 'warning'} size="sm">
                            {status.ssl_provisioned ? 'READY' : 'PROVISIONING'}
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>
                </ListingItem>
              );
            }}
            emptyMessage="No organizational domains identified in the registry"
          />
        </Section>
      </div>

      {/* Live polling indicator */}
      {autoPolling && (
        <div style={{
          marginTop: '3.5rem',
          padding: '1.25rem 1.5rem',
          backgroundColor: 'var(--surface-lvl1)',
          border: '1px solid var(--color-border-light)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          fontSize: '0.95rem',
          color: 'var(--color-primary)'
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: 'currentColor',
            animation: 'pulse 1.5s infinite'
          }}></div>
          Synchronizing domain telemetry across edge nodes...
        </div>
      )}

      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={handleConfirmDelete}
          title="TERMINATE DOMAIN"
          message={`Are you sure you want to terminate domain "${showDeleteModal.domain}"?`}
          details="All connectivity through this endpoint will be immediately revoked. This action is irreversible."
          variant="danger"
          confirmText="Terminate Domain"
          cancelText="Cancel"
          loading={submitting}
        />
      )}
    </AdminPageLayout>
  );
};

export default DomainManagement;
